// pages/ClientGroupsAdminPanel.js
// Admin tab inside /admin for Client Group Maintenance.
// GET  /api/dgIngestSources → current snapshot (loaded on mount)
// POST /api/dgIngestSources → runs the sync (PE roster + live knuula.* + billing
//                             groups; reconcile client_groups; clear Knuula-drifted
//                             review stamps; rebuild DQ)
//
// The daily run comes from a Logic App calling POST /api/dgSyncTick (shared-secret
// header) — SWA managed Functions are HTTP-only, so there's no in-app timer. This
// panel is the manual trigger + observability window.

import { useEffect, useRef, useState } from 'react';
import { useNameFor } from './sections/groups/useNameFor';
import './ClientGroupsAdminPanel.css';

// Friendly copy per DQ issue_code. Keep in sync with clientGroupsIngest.js.
const ISSUE_META = {
  ORPHAN_CLIENT_RG: {
    label: 'Orphan client (no RG)',
    note:  'Active client has no Relationship Group yet. Needs an RG assignment.',
  },
  KNUULA_MISSING_SG: {
    label: 'Knuula missing signer group',
    note:  'Client has no signer group and no live Knuula group (likely never received a letter).',
  },
  SG_SOURCE_DISAGREEMENT: {
    label: 'SG source disagreement (Knuula vs PE / stored)',
    note:  'Knuula disagrees with PE’s signer-group hint, or with the stored signer group of a client whose group Knuula has drifted away from. Partner picks the truth.',
  },
  SG_NEEDS_CONFIRMATION: {
    label: 'SG needs partner confirmation',
    note:  'A signer group is suggested (Knuula preferred) but no partner has confirmed it yet.',
  },
  SG_TRUST_ESTATE_MIXED: {
    label: 'Estate and Trust together with an Individual',
    note:  'A group with an Individual may hold Estate clients or Trust clients, but not both kinds. Split the Estate(s) or the Trust(s) out.',
  },
  SG_NON_SMLLC_MIXED: {
    label: 'Non-SMLLC business with an Individual/Estate/Trust in same SG',
    note:  'Only an LLC, LLP, Sole Proprietor or Partnership can be a single-member LLC on a personal return. Any other business type cannot share a signer group with an Individual, Estate or Trust. Split it out.',
  },
  SG_BUSINESS_INDIVIDUAL_MIXED: {
    label: 'Business or Estate/Trust mixed with an Individual in same SG',
    note:  'A Business with an Individual/Estate/Trust, or an Estate/Trust with an Individual. Legitimate only if the Business is a SMLLC on the personal return (or the Estate/Trust belongs with that Individual). Needs confirmation or split.',
  },
  SG_MULTI_INDIVIDUAL_PARENT: {
    label: 'Individual child under Individual parent',
    note:  'Legitimate only for dependent minors. Needs confirmation.',
  },
  NLC_PARENT_NEEDS_SUCCESSOR: {
    label: 'Group parent marked NLC — new parent needed',
    note:  'A client marked No Longer Client led a signer group or anchored a relationship group that still has active clients. A partner must choose who leads it now.',
  },
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function fmtNumber(n) {
  if (n == null) return '—';
  return n.toLocaleString();
}

// Skipped counts are only known after a live ingest POST. GET returns null
// because we don't persist them across sessions.
function fmtSkipped(n) {
  if (n == null) return <span className="cg-cell-hint">—</span>;
  if (n === 0)   return <span className="cg-cell-hint">0</span>;
  return <span className="cg-severity cg-severity-warn" title="Rows dropped because identifiers didn't match #####-### format">{n.toLocaleString()}</span>;
}

function pct(part, total) {
  if (!total) return '';
  return ` (${Math.round((part / total) * 100)}%)`;
}

export default function ClientGroupsAdminPanel({ flash }) {
  const nameFor = useNameFor();   // e-mails shown as real names (falls back to the e-mail only for someone not in the roster)
  const [status,  setStatus]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err,     setErr]     = useState(null);

  // Firm-wide ingest config — the always-exclude office list.
  const [officesInput,  setOfficesInput]  = useState('');
  const savedOffices = useRef('');             // the list the server last confirmed (what a failed save reverts to)
  const saveChain = useRef(Promise.resolve()); // saves run one after another
  const [officesMsg,    setOfficesMsg]    = useState(null);

  async function loadConfig() {
    try {
      const res = await fetch('/api/dgIngestConfig', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cfg = await res.json();
      const list = Array.isArray(cfg.excludeOffices) ? cfg.excludeOffices : [];
      savedOffices.current = list.join(', ');
      setOfficesInput(savedOffices.current);
    } catch (e) {
      // Non-fatal: leave the editor blank so the admin can still set it.
      console.warn('[ClientGroupsAdminPanel] load ingest config failed:', e.message);
    }
  }

  // Optimistic: the list and the "Saved" note show at once; the POST runs in order behind any earlier
  // save, and a refusal puts the last confirmed list back and shows the server's reason.
  function saveOffices() {
    const list = officesInput.split(',').map(s => s.trim()).filter(Boolean);
    const shown = list.join(', ');
    setOfficesInput(shown);
    setOfficesMsg({ ok: true, text: 'Saved. Run an ingest below to apply it.' });
    if (flash) flash('Excluded offices saved — run an ingest to apply.');
    saveChain.current = saveChain.current.then(async () => {
      try {
        const res = await fetch('/api/dgIngestConfig', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ excludeOffices: list }),
        });
        if (!res.ok) {
          const text = await res.text();
          let msg = text;
          try { msg = JSON.parse(text).error || text; } catch { /* plain text */ }
          throw new Error(msg || `HTTP ${res.status}`);
        }
        const saved = await res.json();
        const savedText = (Array.isArray(saved.excludeOffices) ? saved.excludeOffices : []).join(', ');
        savedOffices.current = savedText;
        setOfficesInput(cur => (cur === shown ? savedText : cur));   // the server's normalized list, unless they've kept typing
      } catch (e) {
        const back = savedOffices.current;
        setOfficesInput(cur => (cur === shown ? back : cur));
        setOfficesMsg({ ok: false, text: `Couldn't save the excluded offices: ${e.message} The last saved list was restored.` });
        if (flash) flash(`Excluded offices NOT saved: ${e.message}`, true);
      }
    });
  }

  async function loadStatus() {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch('/api/dgIngestSources', { credentials: 'include' });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      setStatus(await res.json());
    } catch (e) {
      setErr(e.message);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadStatus(); loadConfig(); }, []);

  // The sync runs in the background (it can outlast the ~45 s limit on a web request), so the button only STARTS it and
  // this follows the run by polling its status until it finishes.
  const [activeRun, setActiveRun] = useState(null);
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  async function followRun(runId) {
    const deadline = Date.now() + 20 * 60 * 1000;
    while (Date.now() < deadline) {
      await sleep(3000);
      let run = null;
      try {
        const res = await fetch(`/api/dgIngestSources?runId=${encodeURIComponent(runId)}`, { credentials: 'include' });
        if (res.ok) run = (await res.json()).run;
      } catch { /* a dropped poll is not a failed run: keep going */ }
      if (!run) continue;
      setActiveRun(run);
      if (run.status === 'running' && run.heartbeatAgeSec != null && run.heartbeatAgeSec > 300) {
        setActiveRun(null);
        throw new Error('The sync stopped reporting (no sign of life for 5 minutes), so its worker was probably restarted. Nothing was changed; you can run it again.');
      }
      if (run.status === 'ok') {
        setActiveRun(null);
        await loadStatus();
        if (flash) flash(`Sync complete in ${run.elapsedMs ?? '?'} ms.`);
        return;
      }
      if (run.status === 'error') {
        setActiveRun(null);
        throw new Error(run.error || 'The sync failed.');
      }
    }
    throw new Error('Still running after 20 minutes. Check "Last run" again later.');
  }

  async function runNow() {
    if (running) return;
    const ok = window.confirm(
      'Run the Client Groups sync now?\n\n' +
      'This refreshes the PE roster, reads live Knuula groups and billing groups, adds new clients ' +
      '(standalone, awaiting review), clears the review stamp of any client whose signer group Knuula ' +
      'has drifted away from, and regenerates the Data Quality issue list for the active period. ' +
      'It never overwrites an existing grouping, and resolved / deferred issues are preserved.\n\n' +
      'It runs in the background and can take several minutes. You can leave this page open.'
    );
    if (!ok) return;

    setRunning(true);
    setErr(null);
    try {
      const res = await fetch('/api/dgIngestSources', {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = text;
        try { msg = JSON.parse(text).error || text; } catch { /* plain text */ }
        throw new Error(msg || `HTTP ${res.status}`);
      }
      const { runId, existing } = await res.json();
      if (existing && flash) flash('A sync is already running. Following it.');
      await followRun(runId);
    } catch (e) {
      setErr(e.message);
      if (flash) flash(`Sync failed: ${e.message}`, true);
    } finally {
      setRunning(false);
    }
  }

  // Page opened (or refreshed) while a sync is running: pick it up instead of showing an idle button.
  useEffect(() => {
    const lr = status && status.lastRun;
    if (lr && (lr.status === 'pending' || lr.status === 'running') && !running) {
      setRunning(true);
      followRun(lr.id)
        .catch(e => { setErr(e.message); if (flash) flash(`Sync failed: ${e.message}`, true); })
        .finally(() => setRunning(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status && status.lastRun && status.lastRun.id]);

  if (loading && !status) {
    return <div className="admin-panel"><p>Loading client groups status…</p></div>;
  }

  if (err && !status) {
    return (
      <div className="admin-panel">
        <h3>Client Groups</h3>
        <div className="admin-flash error">Error loading status: {err}</div>
        <button className="admin-btn" onClick={loadStatus}>Retry</button>
      </div>
    );
  }

  const { period, pe, knuula, clientGroups, dqBreakdown, dqTotalOpen, lastRun } = status || {};
  const cg = clientGroups || {};

  return (
    <div className="admin-panel">
      <h3>Client Groups</h3>
      <p className="admin-hint">
        Manual control surface for the Client Groups sync. The stored <code>client_groups</code>{' '}
        table is the system of record (partners reviewed it in summer 2026), so the sync never
        overwrites a grouping: it refreshes the PE roster, reads live Knuula groups and billing
        groups, adds new clients (standalone, awaiting review), clears the review stamp of
        clients whose signer group Knuula has drifted away from, and regenerates the Data
        Quality issue list for the active period. A Logic App also runs it daily.
      </p>

      <div className="cg-future-note">
        <strong>Ongoing validation:</strong> this page is the <em>manual</em> trigger.
        The daily run comes from an Azure Logic App calling <code>POST /api/dgSyncTick</code> with
        the shared-secret header <code>x-di-snapshot-key</code> — Static Web Apps' managed Functions
        are HTTP-only, so there's no in-app timer.
      </div>

      <div className="cg-exclude-offices">
        <h4>Excluded offices</h4>
        <p className="admin-hint">
          Clients in these offices are <strong>always dropped</strong> from the Client Groups
          ingest — e.g. the employee (<code>EMP</code>) and family (<code>FAM</code>) internal
          offices. Enter comma-separated office codes; matching is case-insensitive. This is a
          firm-wide setting (applies to every period). <strong>Changes take effect on the next
          ingest</strong> — save here, then run an ingest below.
        </p>
        <div className="cg-exclude-row" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="cg-exclude-input"
            value={officesInput}
            onChange={e => setOfficesInput(e.target.value)}
            placeholder="e.g. FAM, EMP"
            style={{ flex: '1 1 260px', minWidth: 200, padding: '6px 10px' }}
          />
          <button className="admin-btn" onClick={saveOffices}>
            Save excluded offices
          </button>
        </div>
        {officesMsg && (
          <div className={`admin-flash ${officesMsg.ok ? '' : 'error'}`} style={{ marginTop: 8 }}>
            {officesMsg.text}
          </div>
        )}
      </div>

      <div className="cg-run-row">
        <div>
          <div className="cg-run-label">Active period</div>
          <div className="cg-run-value"><code>{period || '—'}</code></div>
        </div>
        <div>
          <div className="cg-run-label">Last run</div>
          <div className="cg-run-value">
            {activeRun
              ? `Running since ${fmtDateTime(activeRun.startedAt || activeRun.requestedAt)} (${Math.round((activeRun.elapsedMs || 0) / 1000)} s)…`
              : lastRun
                ? `${fmtDateTime(lastRun.startedAt || lastRun.requestedAt)} · ${lastRun.elapsedMs != null ? `${lastRun.elapsedMs} ms · ` : ''}${nameFor(lastRun.triggeredBy) || ''}${lastRun.status === 'error' ? ` · FAILED: ${lastRun.error || 'see server log'}` : ''}${lastRun.status === 'ok' && lastRun.peRefreshed === false ? ' · PE data NOT refreshed (last good copy used)' : ''}${lastRun.status === 'ok' && Array.isArray(lastRun.warnings) && lastRun.warnings.length ? ` · WARNING: ${lastRun.warnings.join('; ')}` : ''}`
                : 'No run recorded'}
          </div>
        </div>
        <button
          className="admin-btn cg-run-btn"
          onClick={runNow}
          disabled={running}
        >
          {running ? 'Running…' : 'Run ingest now'}
        </button>
      </div>

      {err && status && (
        <div className="admin-flash error" style={{ marginTop: 12 }}>{err}</div>
      )}

      <h4>Sources</h4>
      <table className="admin-table">
        <thead>
          <tr><th>Source</th><th>Rows staged</th><th>Skipped (last run)</th><th>Ingested at</th><th>Notes</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>PE signer groups</strong></td>
            <td>{fmtNumber(pe?.rows)}</td>
            <td>{fmtSkipped(pe?.skipped)}</td>
            <td>{fmtDateTime(pe?.ingestedAt)}</td>
            <td className="cg-cell-hint">
              Source of truth for the active client set. Rows are dropped if <code>ClientCode</code> doesn&apos;t match <code>#####-###</code>.
              {pe?.officeFiltered != null && (
                <> Excluded-office rows dropped last run: <strong>{fmtNumber(pe.officeFiltered)}</strong>.</>
              )}
            </td>
          </tr>
          <tr>
            <td><strong>Knuula signer groups</strong></td>
            <td>{fmtNumber(knuula?.rows)}</td>
            <td>{fmtSkipped(knuula?.skipped)}</td>
            <td>{fmtDateTime(knuula?.ingestedAt)}</td>
            <td className="cg-cell-hint">Live from <code>knuula.PartyGroupMember</code> (group key = <code>GroupLookupKey</code>) — a cross-check and new-client seed, not the source of truth. A client in several Knuula groups is staged with the one matching its stored SG, else the largest.</td>
          </tr>
        </tbody>
      </table>

      <h4>Client Groups table</h4>
      <div className="cg-stats">
        <div className="cg-stat-card">
          <div className="cg-stat-label">Total rows</div>
          <div className="cg-stat-value">{fmtNumber(cg.total)}</div>
        </div>
        <div className="cg-stat-card">
          <div className="cg-stat-label">Active</div>
          <div className="cg-stat-value">{fmtNumber(cg.active)}</div>
        </div>
        <div className="cg-stat-card">
          <div className="cg-stat-label">SG reviewed</div>
          <div className="cg-stat-value">
            {fmtNumber(cg.sgReviewed)}<span className="cg-stat-pct">{pct(cg.sgReviewed, cg.active)}</span>
          </div>
        </div>
        <div className="cg-stat-card">
          <div className="cg-stat-label">RG reviewed</div>
          <div className="cg-stat-value">
            {fmtNumber(cg.rgReviewed)}<span className="cg-stat-pct">{pct(cg.rgReviewed, cg.active)}</span>
          </div>
        </div>
        <div className="cg-stat-card">
          <div className="cg-stat-label">With RG seeded</div>
          <div className="cg-stat-value">
            {fmtNumber(cg.withRg)}<span className="cg-stat-pct">{pct(cg.withRg, cg.active)}</span>
          </div>
        </div>
        <div className="cg-stat-card">
          <div className="cg-stat-label">With SG confirmed</div>
          <div className="cg-stat-value">
            {fmtNumber(cg.withSg)}<span className="cg-stat-pct">{pct(cg.withSg, cg.active)}</span>
          </div>
        </div>
      </div>

      <h4>Data quality issues — active period ({fmtNumber(dqTotalOpen || 0)} open)</h4>
      {(dqBreakdown || []).length === 0 ? (
        <p className="admin-hint">No open DQ issues for the active period. Nice.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: '32%' }}>Issue</th>
              <th style={{ width: '10%' }}>Severity</th>
              <th style={{ width: '10%' }}>Count</th>
              <th>What it means</th>
            </tr>
          </thead>
          <tbody>
            {dqBreakdown.map(row => {
              const meta = ISSUE_META[row.issueCode] || {};
              return (
                <tr key={row.issueCode}>
                  <td>
                    <div className="cg-issue-label">{meta.label || row.issueCode}</div>
                    <div className="cg-issue-code"><code>{row.issueCode}</code></div>
                  </td>
                  <td>
                    <span className={`cg-severity cg-severity-${row.severity.toLowerCase()}`}>
                      {row.severity}
                    </span>
                  </td>
                  <td><strong>{fmtNumber(row.count)}</strong></td>
                  <td className="cg-cell-hint">{meta.note || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <CrossBookChanges />
    </div>
  );
}

// "Changed outside someone's CP book" — the daily report from dataIntegrity.cg_change_log (sql/051), written by
// diResolveAction. It only records: nothing here blocks a change. GET /api/diCgChangeReport (admin).
const WINDOWS = [[24, 'Last 24 hours'], [72, 'Last 3 days'], [168, 'Last 7 days'], [720, 'Last 30 days']];

function whyFlagged(r, nameFor = (x) => x) {
  const why = [];
  if (r.crossBook) why.push(`Not their client (CP: ${nameFor(r.ownerCp)})`);
  if (r.otherCpInGroup) why.push('Now shares a group with another CP’s clients');
  return why.length ? why : ['Their own client'];
}

function CrossBookChanges() {
  const nameFor = useNameFor();
  const [hours, setHours] = useState(24);
  const [hideAdmins, setHideAdmins] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    const qs = new URLSearchParams({ hours: String(hours), hideAdmins: hideAdmins ? '1' : '0', all: showAll ? '1' : '0' });
    fetch(`/api/diCgChangeReport?${qs}`, { credentials: 'include' })
      .then(async res => { if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`); return res.json(); })
      .then(body => { if (alive) { setData(body); setErr(null); } })
      .catch(e => { if (alive) setErr(e.message); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [hours, hideAdmins, showAll]);

  const rows = (data && data.rows) || [];
  const s = (data && data.summary) || { changes: 0, actions: 0, byActor: [], byOwner: [] };

  return (
    <div className="cg-crossbook">
      <h4>Changes outside someone&rsquo;s CP book</h4>
      <p className="admin-hint">
        Every Resolve action is logged with who made it and whose client it changed. This list shows changes to a client whose
        CP is not the person acting, and changes that put a client into a group holding another CP&rsquo;s clients. It is a
        record only; nothing is blocked.
      </p>
      <div className="cg-crossbook-controls">
        <select id="cg-cb-window" value={hours} onChange={e => setHours(Number(e.target.value))} aria-label="Time window">
          {WINDOWS.map(([h, label]) => <option key={h} value={h}>{label}</option>)}
        </select>
        <label><input id="cg-cb-hide-admins" type="checkbox" checked={hideAdmins} onChange={e => setHideAdmins(e.target.checked)} /> Hide admin changes</label>
        <label><input id="cg-cb-show-all" type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} /> Show every logged change</label>
      </div>

      {err && <div className="admin-flash error">Couldn&rsquo;t load the report: {err}</div>}
      {data && data.installed === false && (
        <p className="admin-hint">The change log isn&rsquo;t set up yet. Run <code>sql/051-cg-change-log.sql</code>; changes made before it is run are not recorded.</p>
      )}
      {data && data.installed !== false && !err && (
        <>
          <p className="admin-hint">
            {busy ? 'Refreshing… ' : ''}
            <strong>{s.changes}</strong> client change{s.changes === 1 ? '' : 's'} from <strong>{s.actions}</strong> action{s.actions === 1 ? '' : 's'}.
            {s.byOwner.length > 0 && <> Most affected CP: <strong>{nameFor(s.byOwner[0].owner)}</strong> ({s.byOwner[0].changes}).</>}
          </p>
          {rows.length === 0 ? (
            <p className="admin-hint">Nothing in this window.</p>
          ) : (
            <div className="cg-crossbook-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>When</th><th>Who</th><th>Action</th><th>Client</th><th>Why it&rsquo;s listed</th><th>Group (SG / RG)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td className="cg-cell-hint">{fmtDateTime(r.at)}</td>
                      <td>
                        {nameFor(r.actor)}
                        {r.realEmail && r.realEmail !== r.actor && <div className="cg-cell-hint">via {nameFor(r.realEmail)}</div>}
                        {r.actorIsAdmin && !r.realEmail && <div className="cg-cell-hint">admin</div>}
                      </td>
                      <td><code>{r.verb}</code>{r.issueCode && <div className="cg-cell-hint">{r.issueCode}</div>}</td>
                      <td><code>{r.clientCode}</code>{r.after.active === false && <div className="cg-cell-hint">now inactive</div>}</td>
                      <td>{whyFlagged(r, nameFor).map(w => <div key={w}>{w}</div>)}</td>
                      <td className="cg-cell-hint">
                        {(r.before.sg || '—')} / {(r.before.rg || '—')} &rarr; {(r.after.sg || '—')} / {(r.after.rg || '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
